<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import L from 'leaflet'
import QRCode from 'qrcode'
import { GOOGLE_STREET } from '@/store/mapConstants'
import { DEFAULT_PRINT_THEME } from '@/composables/usePrintTheme'

const props = defineProps({
  salida: { type: Object, default: () => ({}) },
  territories: { type: Array, default: () => [] },
  congregacionName: String,
  conductores: { type: Array, default: () => [] },
  grupos: { type: Array, default: () => [] },
  zonas: { type: Array, default: () => [] },
  token: String,
  printTheme: { type: Object, default: () => ({}) },
})
defineEmits(['close'])

// Colores/tipografía/logo personalizables por congregación (ver Parametros →
// Impresión de salidas). Se aplican como CSS custom properties sobre el
// overlay para no tocar el resto del layout/paginación de la hoja A4.
const theme = computed(() => ({ ...DEFAULT_PRINT_THEME, ...(props.printTheme || {}) }))

const themeStyleVars = computed(() => ({
  '--print-accent-color': theme.value.accentColor || DEFAULT_PRINT_THEME.accentColor,
  '--print-note-bg': theme.value.noteBg || DEFAULT_PRINT_THEME.noteBg,
  '--print-note-color': theme.value.noteColor || DEFAULT_PRINT_THEME.noteColor,
  '--print-font-family': theme.value.fontFamily || 'var(--font-system)',
}))

const logoSrc = computed(() => theme.value.logoUrl || '/territorios-icono.png')

const getTerrKey = (t) => `${t.zona || ''}${t.numero || ''}`

const conductorName = computed(() => {
  const found = props.conductores?.find((c) => c.id === props.salida?.conductor)
  return found ? found.name : (props.salida?.conductor || '')
})

const gruposStr = computed(() => {
  const ids = props.salida?.grupos
  if (!ids?.length) return ''
  return ids
    .map((gId) => {
      const found = props.grupos?.find((g) => g.id === gId)
      const name = found ? found.name : gId
      const zona = found?.zonaId ? props.zonas?.find((z) => z.id === found.zonaId)?.name : null
      return zona ? `${name} — ${zona}` : name
    })
    .join(', ')
})

const dayName = computed(() => {
  if (!props.salida?.date) return ''
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  return days[new Date(props.salida.date).getDay()]
})

const fechaStr = computed(() =>
  props.salida?.date ? new Date(props.salida.date).toLocaleDateString('es-AR') : ''
)

const chipStyle = (t) =>
  `background:${t.color?.backgroundColor || '#ccc'};color:${t.color?.color || '#000'}`

// ─── QR por territorio: tres accesos independientes y bien simples ──────────
// - Detalle: solo lista qué manzanas faltan trabajar (de lectura, con logo).
// - Informar: solo el formulario para marcar manzanas y registrar el trabajo.
// - Mapa: el mapa interactivo completo, centrado en el territorio.
const qrDetalle = ref({})
const qrInformar = ref({})
const qrMapa = ref({})

const buildQrs = async () => {
  if (!props.token || !props.salida?.id) return
  for (const t of props.territories) {
    const key = getTerrKey(t)
    const base = `${window.location.origin}/salida-publica?token=${props.token}&salida=${props.salida.id}&territorio=${encodeURIComponent(key)}`
    try {
      const [detalle, informar, mapa] = await Promise.all([
        QRCode.toDataURL(`${base}&accion=detalle`, { margin: 1, width: 220 }),
        QRCode.toDataURL(`${base}&accion=informar`, { margin: 1, width: 220 }),
        QRCode.toDataURL(base, { margin: 1, width: 220 }),
      ])
      qrDetalle.value = { ...qrDetalle.value, [key]: detalle }
      qrInformar.value = { ...qrInformar.value, [key]: informar }
      qrMapa.value = { ...qrMapa.value, [key]: mapa }
    } catch {
      // Sin QR para este territorio si falla la generación; el resto de la hoja sigue útil.
    }
  }
}

// ─── Mapas estáticos (uno por territorio, sin interacción), con tiles de Google ──
// Dos mapas por territorio: el principal (grande, con las manzanas numeradas,
// en la página del territorio) y uno mini de referencia (sin manzanas, bien
// alejado, en la página de detalles) para ubicar el territorio dentro del
// barrio de un vistazo sin robarle espacio al mapa grande.
const mapEls = {}
const leafletMaps = {}
const miniMapEls = {}
const leafletMiniMaps = {}

const setMapEl = (key, el) => {
  if (el) mapEls[key] = el
}

const setMiniMapEl = (key, el) => {
  if (el) miniMapEls[key] = el
}

const toLatlng = (v) => {
  if (!v) return null
  if (Array.isArray(v)) return [v[0], v[1]]
  return [Number(v.lat), Number(v.lng)]
}

const addGoogleTiles = (map) =>
  L.tileLayer(GOOGLE_STREET.url, {
    attribution: GOOGLE_STREET.attribution,
    maxZoom: GOOGLE_STREET.maxZoom ?? 20,
    subdomains: GOOGLE_STREET.subdomains,
  }).addTo(map)

const STATIC_MAP_OPTS = {
  zoomControl: false,
  dragging: false,
  scrollWheelZoom: false,
  doubleClickZoom: false,
  boxZoom: false,
  keyboard: false,
  tap: false,
  touchZoom: false,
  // Zoom fraccionario: sin esto, fitBounds redondea el zoom al entero inferior y
  // los territorios grandes quedan chicos con mucho margen vacío alrededor.
  zoomSnap: 0,
}

const buildMapFor = (t) => {
  const key = getTerrKey(t)
  const el = mapEls[key]
  if (!el || leafletMaps[key] || !t.limits?.length) return
  const coords = t.limits.map(toLatlng).filter(Boolean)
  if (coords.length < 3) return

  const map = L.map(el, STATIC_MAP_OPTS)
  addGoogleTiles(map)

  L.polygon(coords, {
    color: t.color?.backgroundColor || 'rgb(156,156,0)',
    fillColor: t.color?.backgroundColor || 'rgb(252,252,71)',
    fillOpacity: 0.15,
    weight: 2,
  }).addTo(map)

  // Un número por manzana, para poder identificarlas a simple vista en el papel.
  if (Array.isArray(t.mzNumbers)) {
    for (let i = 1; i < t.mzNumbers.length; i++) {
      const entry = t.mzNumbers[i]
      if (!entry?.[0]) continue
      L.marker(entry[0], {
        icon: L.divIcon({
          className: 'print-mz-marker',
          iconSize: null,
          html: `<div class="print-mz-dot">${i}</div>`,
        }),
      }).addTo(map)
    }
  }

  // Poco padding y sin techo de zoom: el contenedor es grande, así que fitBounds
  // ya elige un zoom bien metido para separar las manzanas entre sí. El mapa
  // mini de al lado se encarga de dar contexto/ubicación, no este.
  map.fitBounds(L.latLngBounds(coords).pad(0.04))
  leafletMaps[key] = map

  const mini = miniMapEls[key]
  if (mini && !leafletMiniMaps[key]) {
    const miniMap = L.map(mini, { ...STATIC_MAP_OPTS, attributionControl: false })
    addGoogleTiles(miniMap)
    L.polygon(coords, {
      color: t.color?.backgroundColor || 'rgb(156,156,0)',
      fillColor: t.color?.backgroundColor || 'rgb(156,156,0)',
      fillOpacity: 0.55,
      weight: 2,
    }).addTo(miniMap)
    // Más padding que el mapa principal para dar contexto del barrio, pero sin
    // pasarse: con demasiado el territorio queda como un punto minúsculo y
    // pierde sentido. Un poco más zoomeado para que se note bien.
    miniMap.fitBounds(L.latLngBounds(coords).pad(0.2))
    leafletMiniMaps[key] = miniMap
    setTimeout(() => miniMap.invalidateSize(), 200)
  }

  // El contenedor puede terminar de animarse/layoutear después del fitBounds inicial.
  setTimeout(() => map.invalidateSize(), 200)
}

const buildMaps = async () => {
  await nextTick()
  for (const t of props.territories) buildMapFor(t)
}

onMounted(() => {
  buildMaps()
  buildQrs()
})

onBeforeUnmount(() => {
  Object.values(leafletMaps).forEach((m) => m.remove())
  Object.values(leafletMiniMaps).forEach((m) => m.remove())
})

const doPrint = () => window.print()
</script>

<template>
  <div class="print-overlay" :style="themeStyleVars">
    <div class="print-toolbar noprint">
      <button class="toolbar-btn toolbar-close" @click="$emit('close')">
        <i class="pi pi-times" />
        Cerrar
      </button>
      <span class="toolbar-title">Vista de impresión</span>
      <button class="toolbar-btn toolbar-print" @click="doPrint">
        <i class="pi pi-print" />
        Imprimir
      </button>
    </div>

    <div class="print-pages">
      <!-- Una página por territorio a predicar -->
      <section v-for="t in territories" :key="getTerrKey(t)" class="hoja">
        <header class="hoja-header">
          <img :src="logoSrc" alt="Logo" class="hoja-logo">
          <div class="hoja-header-text">
            <h1>Territorio a predicar</h1>
            <span class="terr-chip" :style="chipStyle(t)">{{ getTerrKey(t) }}</span>
          </div>
        </header>

        <div :ref="(el) => setMapEl(getTerrKey(t), el)" class="print-map" />

        <div v-if="t.referencia || t.notas" class="terr-info-block">
          <p v-if="t.referencia" class="terr-ref">{{ t.referencia }}</p>
          <p v-if="t.notas" class="terr-notas">{{ t.notas }}</p>
        </div>

        <div class="qr-links-row">
          <div class="qr-link-col">
            <img
              v-if="qrDetalle[getTerrKey(t)]"
              :src="qrDetalle[getTerrKey(t)]"
              class="qr-img-sm"
              alt="QR detalle de manzanas faltantes"
            >
            <div class="qr-caption-sm">Ver qué falta<br>trabajar</div>
          </div>
          <div class="qr-link-col">
            <img
              v-if="qrInformar[getTerrKey(t)]"
              :src="qrInformar[getTerrKey(t)]"
              class="qr-img-sm"
              alt="QR para informar lo trabajado"
            >
            <div class="qr-caption-sm">Informar lo<br>trabajado</div>
          </div>
          <div class="qr-link-col">
            <img
              v-if="qrMapa[getTerrKey(t)]"
              :src="qrMapa[getTerrKey(t)]"
              class="qr-img-sm"
              alt="QR mapa interactivo"
            >
            <div class="qr-caption-sm">Ver mapa<br>interactivo</div>
          </div>
        </div>

        <div class="recordatorio-nota">
          <i class="pi pi-bell" />
          Por favor, al finalizar cada mañana o cada tarde de predicación en este territorio, informá lo que predicaron.
        </div>
      </section>

      <!-- Página final: detalles del grupo -->
      <section class="hoja">
        <header class="hoja-header">
          <img :src="logoSrc" alt="Logo" class="hoja-logo">
          <div>
            <h1>{{ congregacionName || 'Detalles de la salida' }}</h1>
            <div class="fecha-line">
              {{ dayName }} {{ fechaStr }}<template v-if="salida.hour"> · {{ salida.hour }} hs</template>
            </div>
          </div>
        </header>

        <div class="grupo-grid">
          <div class="grupo-col">
            <div v-if="conductorName" class="info-row">
              <span class="info-label">Conductor</span>
              <span class="info-value">{{ conductorName }}</span>
            </div>
            <div v-if="gruposStr" class="info-row">
              <span class="info-label">Grupo</span>
              <span class="info-value">{{ gruposStr }}</span>
            </div>
            <div v-if="salida.puntoEncuentroTerritorio?.name || salida.puntoEncuentroTerritorio?.direccion" class="info-row">
              <span class="info-label">Punto en territorio</span>
              <span class="info-value">
                {{ salida.puntoEncuentroTerritorio.name }}
                <template v-if="salida.puntoEncuentroTerritorio.direccion">
                  <br><small>{{ salida.puntoEncuentroTerritorio.direccion }}</small>
                </template>
              </span>
            </div>

            <div v-if="salida.gruposInvitadosData?.length" class="invitados-print">
              <h3>Grupos de invitados</h3>
              <div v-for="g in salida.gruposInvitadosData" :key="g.id" class="invitado-print-row">
                <strong v-if="g.encargado">{{ g.encargado }}</strong>
                — {{ g.cantidad }} persona{{ g.cantidad !== 1 ? 's' : '' }}
                <template v-if="g.autos"> · {{ g.autos }} vehículo{{ g.autos !== 1 ? 's' : '' }}</template>
              </div>
            </div>

            <div v-if="salida.notas" class="notas-print">
              <h3>Notas</h3>
              <p>{{ salida.notas }}</p>
            </div>
          </div>
        </div>

        <div v-if="territories.length" class="mini-maps-section">
          <h3>Ubicación de los territorios</h3>
          <div class="mini-maps-row">
            <div v-for="t in territories" :key="getTerrKey(t) + '-mini'" class="mini-map-item">
              <div :ref="(el) => setMiniMapEl(getTerrKey(t), el)" class="print-map-mini" />
              <div class="print-map-mini-caption">{{ getTerrKey(t) }}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
@page {
  size: A4;
  margin: 0;
}

.print-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: #525659;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.print-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #1c1c1e;
  padding: 10px 16px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}

.toolbar-title {
  color: #fff;
  font-weight: 600;
  font-size: 15px;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.toolbar-close {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.toolbar-print {
  background: #007aff;
  color: #fff;
}

.print-pages {
  padding: 24px 0 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.hoja {
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  box-sizing: border-box;
  background: #fff;
  color: #111;
  padding: 15mm;
  text-align: left;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.35);
  font-family: var(--print-font-family, inherit);
}

.hoja-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 3px solid var(--print-accent-color, #007aff);
  flex-wrap: wrap;
}

.hoja-header-text {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.hoja-header h1 {
  margin: 0;
  font-size: 22px;
  color: #111;
}

.hoja-logo {
  height: 20mm;
  width: auto;
  flex-shrink: 0;
}

.terr-chip {
  padding: 4px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
}

.print-map {
  width: 100%;
  height: 150mm;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 10px;
}

.mini-maps-section {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #ddd;
}

.mini-maps-section h3 {
  font-size: 14px;
  margin: 0 0 12px;
  color: #111;
}

.mini-maps-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.mini-map-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 83mm;
}

.print-map-mini {
  width: 100%;
  height: 83mm;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

.print-map-mini-caption {
  font-size: 12px;
  font-weight: 700;
  color: #111;
}

:deep(.print-mz-dot) {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 1.5px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #111;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.terr-info-block {
  margin-bottom: 16px;
}

.terr-ref,
.terr-notas {
  font-size: 13px;
  margin: 2px 0;
  color: #333;
}

.qr-links-row {
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-top: 10px;
}

.qr-link-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.qr-img-sm {
  width: 28mm;
  height: 28mm;
}

.qr-caption-sm {
  font-size: 11px;
  text-align: center;
  color: #444;
  line-height: 1.3;
}

.recordatorio-nota {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--print-note-bg, #e3f7e8);
  color: var(--print-note-color, #1e7d34);
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.fecha-line {
  font-size: 14px;
  color: #444;
  margin-bottom: 18px;
}

.grupo-grid {
  display: flex;
  gap: 24px;
}

.grupo-col {
  flex: 1;
  min-width: 0;
}

.info-row {
  margin-bottom: 14px;
}

.info-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #777;
  margin-bottom: 2px;
}

.info-value {
  font-size: 15px;
  font-weight: 600;
  color: #111;
}

.info-value small {
  font-size: 12px;
  font-weight: 400;
  color: #555;
}

.invitados-print,
.notas-print {
  margin-top: 18px;
}

.invitados-print h3,
.notas-print h3 {
  font-size: 14px;
  margin: 0 0 6px;
  color: #111;
}

.invitado-print-row {
  font-size: 13px;
  margin-bottom: 4px;
  color: #222;
}

.notas-print p {
  font-size: 13px;
  color: #222;
  white-space: pre-wrap;
}

@media print {
  .print-overlay {
    position: static;
    background: none;
    overflow: visible;
  }

  .print-pages {
    padding: 0;
    gap: 0;
  }

  .hoja {
    box-shadow: none;
    margin: 0;
    width: 210mm;
    height: 297mm;
    min-height: 297mm;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
  }

  .hoja:last-child {
    page-break-after: auto;
    break-after: auto;
  }
}
</style>
